import { IDropdownOption, IDropdownProps, IStackProps, MessageBar, MessageBarType, Stack, Text } from '@fluentui/react';
import ToggleOptions from 'components/controls/ToggleOptions';
import { ECompareVMStatus, EMergeVMStatus, IAttributeSetVM, IEntityVM, INonStandardVM } from 'global/Compare/ComparisonViewModelGenerator';
import React from 'react';
import { IEntityComparePanelProps } from './EntityComparePanel';
import { ExpandableMessageBody } from './ExpandableMessageBody';

/* New Model: What updates are to be made to target based on comparison status and user selection (mergeAction)
1) if entity exists only on target (props.compareVM.status === ECompareStatus.TargetOnly) we can:
    a) remove on target
        * if deactivated, this can be the default
    b) no action 
        * keep target as is
        * this is the default
2) if entity exists only on origin (props.compareVM.status === ECompareStatus.OriginOnly) we can;
    a) pick all origin
        * this is the default
    b) no action
        * keep target as is
3) if entity exists on both origin and target (props.compareVM.status === ECompareStatus.Modified) we can:
    a) pick all origin
    b) pick all target
    c) mix attributes (origin With target)
        * a possible automation could pick any values that are missing on target but exist on origin
    d) no action
        * keep target as is
        * this is the default
*/

interface IMergeControlsProps {
    mergeOptions: IDropdownOption<EMergeVMStatus>[];
    selectedMergeOption?: EMergeVMStatus;
    onChange: IDropdownProps['onChange'];
    onShowDetailsClick?: () => void;
    entityVM: IEntityVM | IAttributeSetVM | INonStandardVM;
    entityProps: IEntityComparePanelProps;
}

const STACK_STYLES: IStackProps['styles'] = {
    root: {
        marginTop: '5px',
        marginBottom: '15px',
    },
}

const STACK_TOKENS: IStackProps['tokens'] = { childrenGap: 8 }

export const STACK_DEFAULT_PROPS: Partial<IStackProps> = {
    tokens: { childrenGap: 8 },
}

export const MESSAGE_PREVIEW_TEXT_STYLES = {
    root: {
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        whiteSpace: 'nowrap' as const,
    },
};

export const MESSAGE_TOGGLE_LINK_STYLES: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    marginTop: 8,
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#0f6cbd',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
};

export const MESSAGE_CONTAINER_STYLES: React.CSSProperties = {
    width: '100%',
};

export const MESSAGE_CONTENT_STYLES: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
};

export interface IExpandableMessageBodyProps {
    messageBarType: MessageBarType;
    summary: React.ReactNode;
    children?: React.ReactNode;
}

const MergeControl: React.FC<IMergeControlsProps> = (props) => {

    const selectedMergeOption = props.selectedMergeOption === EMergeVMStatus.Pend
        ? undefined
        : props.selectedMergeOption;

    const mergeDisabled = props.selectedMergeOption === EMergeVMStatus.ParentOverride ||
        props.selectedMergeOption === EMergeVMStatus.ParentOverrideUseOrigin ||
        props.selectedMergeOption === EMergeVMStatus.ParentOverrideUseTarget;

    return (
        <Stack
            tokens={STACK_TOKENS}
            styles={STACK_STYLES}
        >
            {/* <Stack horizontal
                tokens={{ childrenGap: 8 }}
                verticalAlign='end'
            >
                <Stack.Item grow>
                    <Dropdown
                        label='Merge Action'
                        options={props.mergeOptions}
                        selectedKey={props.selectedMergeOption}
                        onChange={props.onChange}
                        disabled={mergeDisabled}
                    />
                </Stack.Item>

                {props.selectedMergeOption !== EMergeAction.Merge && props.onShowDetailsClick && (
                    <DefaultButton
                        styles={{ root: { fontStyle: 'italic' } }}
                        onClick={props.onShowDetailsClick}
                    >
                        Show Details
                    </DefaultButton>
                )}
            </Stack> */}

            {!mergeDisabled &&
                <Stack.Item grow>
                    <ToggleOptions
                        label={(mergeDisabled ? 'Merge Action (Inherited)' : 'Select Merge Action:')}
                        defaultSelectedKey={selectedMergeOption}
                        clearHidden={true}
                        options={props.mergeOptions}
                        disabled={mergeDisabled}
                        optionsClassName="merge-toggle-options"
                        onChange={(event, option) => {
                            props.onChange?.(event as any, option);
                        }}
                    />
                </Stack.Item>
            }
            {/* 
            {props.entityProps.configuration.mergeShowInstructions && !mergeDisabled &&
                <Separator />
            } */}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.Pend && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    <Text>
                        Merge action is required. Select an option before previewing or saving the merge.
                    </Text>
                </MessageBar>
            )}

            {props.entityProps.configuration.mergeShowInstructions && mergeDisabled && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.info}
                    summary='This entity inherits its merge behavior from a parent entity.'
                >
                    <Text>
                        Its merge behavior is determined by the parent entity and cannot be changed at this level.
                    </Text>
                    <Text>
                        Refer to the parent entity for merge details and available actions.
                    </Text>
                </ExpandableMessageBody>
            )}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.UseTarget && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.info}
                    summary={props.entityVM.compareResult === ECompareVMStatus.OnOriginOnly
                        ? 'This entity exists only in the origin.'
                        : 'This entity will not be modified.'}
                >
                    {props.entityVM.compareResult === ECompareVMStatus.OnOriginOnly && (
                        <Text>
                            It will not be added to the target during the merge.
                        </Text>
                    )}
                    {props.entityVM.compareResult !== ECompareVMStatus.OnOriginOnly && (
                        <Text>
                            The target version will be preserved during the merge.
                        </Text>
                    )}
                </ExpandableMessageBody>
            )}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.RemoveTarget && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.error}
                    summary='This entity will be removed from the target.'
                >
                    <Text>
                        This entity exists only in the target and has no corresponding entity in the origin.
                    </Text>
                </ExpandableMessageBody>
            )}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.AddOrigin && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.success}
                    summary='This entity will be added to the target from the origin.'
                >
                    <Text>
                        This entity exists only in the origin. It will be added to the target when the merge is completed.
                    </Text>
                    <Text>
                        If this entity has any sub-entities, they will also be added to the target.
                    </Text>
                </ExpandableMessageBody>
            )}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.UseOrigin && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.severeWarning}
                    summary='This entity will be replaced with the version from the origin.'
                >
                    <Text>
                        The entity exists in both the origin and target. After the merge, all values from the origin will overwrite those in the target.
                    </Text>
                    <Text>
                        Related sub-entities, including nonstandards and attribute sets, will also be overwritten with data from the origin.
                    </Text>
                </ExpandableMessageBody>
            )}

            {props.entityProps.configuration.mergeShowInstructions && props.selectedMergeOption === EMergeVMStatus.Merge && (
                <ExpandableMessageBody
                    messageBarType={MessageBarType.warning}
                    summary='This entity will be merged from the origin and target. (Flexible Attributes/Individual Non-standards/Individual Attribute Sets)'
                >
                    <Text>
                        This entity exists in both the origin and target.
                    </Text>
                    <Text>
                        Properties from target will be preserved where they differ from origin.
                    </Text>
                    <Text>
                        For each Flexible Attribute, select either Origin or Target.
                    </Text>
                    <Text>
                        For each non-standard or attribute set field, select the desired Merge Action.
                    </Text>
                </ExpandableMessageBody>
            )}

        </Stack>
    );
};

export default MergeControl;